import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUserContext } from './UserContext';
import { getUserById, addFavorite, deleteFavorite } from '../services/UserServices';

// Storefront UI state shared across pages: the user's favourites (loaded once) and which
// overlay is open (quick add, cart drawer, search panel). The overlays render in ShopOverlays.
const ShopUIContext = createContext();

export const ShopUIProvider = ({ children }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useUserContext();
    const [favorites, setFavorites] = useState(() => new Set());
    const [quickAddProduct, setQuickAddProduct] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        if (!currentUser) { setFavorites(new Set()); return; }
        getUserById(currentUser)
            .then(({ user }) => setFavorites(new Set((user?.favorites || []).filter(Boolean))))
            .catch(() => { });
    }, [currentUser]);

    // Close overlays when the page changes
    useEffect(() => { setQuickAddProduct(null); setCartOpen(false); setSearchOpen(false); }, [location.pathname]);

    const isFavorite = useCallback((id) => favorites.has(id), [favorites]);

    const toggleFavorite = useCallback((productId) => {
        if (!currentUser) {
            navigate('/login', { state: { from: location.pathname } });
            return;
        }
        const on = !favorites.has(productId);
        setFavorites(prev => { const next = new Set(prev); on ? next.add(productId) : next.delete(productId); return next; });
        (on ? addFavorite : deleteFavorite)(currentUser, productId).catch(() => {
            setFavorites(prev => { const next = new Set(prev); on ? next.delete(productId) : next.add(productId); return next; });
        });
    }, [currentUser, favorites, navigate, location.pathname]);

    const value = {
        favorites, isFavorite, toggleFavorite,
        quickAddProduct, openQuickAdd: setQuickAddProduct, closeQuickAdd: () => setQuickAddProduct(null),
        cartOpen, openCart: () => setCartOpen(true), closeCart: () => setCartOpen(false),
        searchOpen, openSearch: () => setSearchOpen(true), closeSearch: () => setSearchOpen(false),
    };

    return <ShopUIContext.Provider value={value} >{children}</ShopUIContext.Provider>;
};

export const useShopUI = () => useContext(ShopUIContext);
